import type { LeasingFunnelStep } from "api/crm-analytics";

// The classic funnel silhouette: one trapezoid per step, its top edge sized by
// that step's share of all leads created and its bottom edge by the next
// step's. Widths are drawn from the total rather than from the previous step,
// so the taper mirrors the drop-off instead of flattening it out.
const MIN_BAND_WIDTH = 8;
const BAND_GAP = 3;
const BASE_OPACITY = 1;
const OPACITY_STEP = 0.1;
const MIN_OPACITY = 0.3;

const bandOpacity = (index: number) =>
  Math.max(MIN_OPACITY, BASE_OPACITY - index * OPACITY_STEP);

export const FunnelBands = ({
  steps,
  total,
  width,
  rowHeight,
  hoveredKey
}: {
  steps: LeasingFunnelStep[];
  total: number;
  width: number;
  rowHeight: number;
  hoveredKey: string | null;
}) => {
  const height = steps.length * rowHeight;
  const center = width / 2;
  const widthOf = (count: number) =>
    total > 0
      ? Math.max(MIN_BAND_WIDTH, (count / total) * (width - MIN_BAND_WIDTH))
      : MIN_BAND_WIDTH;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="block"
      aria-hidden="true"
    >
      {steps.map((step, index) => {
        const topWidth = widthOf(step.count);
        const bottomWidth = widthOf(steps[index + 1]?.count ?? step.count);
        const top = index * rowHeight + BAND_GAP / 2;
        const bottom = (index + 1) * rowHeight - BAND_GAP / 2;
        const dimmed = hoveredKey !== null && hoveredKey !== step.key;

        return (
          <path
            key={step.key}
            d={[
              `M${center - topWidth / 2},${top}`,
              `L${center + topWidth / 2},${top}`,
              `L${center + bottomWidth / 2},${bottom}`,
              `L${center - bottomWidth / 2},${bottom}`,
              "Z"
            ].join("")}
            fill="var(--primary-500)"
            fillOpacity={dimmed ? 0.25 : bandOpacity(index)}
            className="transition-opacity"
          />
        );
      })}
    </svg>
  );
};
